import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'sc_cart_v1';

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Tiered shipping rules: based on TOTAL quantity of physical items in cart
export const computeShipping = (physicalQty) => {
  if (physicalQty <= 0) return 0;
  if (physicalQty <= 2) return 4.95;
  if (physicalQty <= 5) return 5.95;
  return 6.95;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  // item shape: { album_id, variant, title, price, image_url, has_physical, shipping_fee, quantity }
  const addItem = (product, variant = 'digital', quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (it) => it.album_id === product.id && it.variant === variant
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          album_id: product.id,
          variant,
          title: product.title || product.title_es,
          title_es: product.title_es,
          price: product.price,
          image_url: product.image_url,
          item_type: product.item_type,
          has_physical: !!product.has_physical,
          shipping_fee: product.shipping_fee ?? 4.95,
          quantity,
        },
      ];
    });
  };

  const updateQuantity = (album_id, variant, quantity) => {
    setItems((prev) =>
      prev
        .map((it) =>
          it.album_id === album_id && it.variant === variant
            ? { ...it, quantity: Math.max(1, quantity) }
            : it
        )
        .filter((it) => it.quantity > 0)
    );
  };

  const removeItem = (album_id, variant) => {
    setItems((prev) =>
      prev.filter((it) => !(it.album_id === album_id && it.variant === variant))
    );
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const physicalQty = items
    .filter((it) => it.variant === 'physical')
    .reduce((sum, it) => sum + it.quantity, 0);
  const shipping = computeShipping(physicalQty);
  const totalQuantity = items.reduce((sum, it) => sum + it.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        subtotal,
        shipping,
        physicalQty,
        totalQuantity,
        total: subtotal + shipping,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
